import React from 'react';

import { useAIAnswerContext } from '../ai-answer-dialog/ai-answer-context';
import { SAINFeatureControl } from '../common/utils/sain-feature-control';
import { QueryIntent } from '../constants';
import { JiraNlToJqlCard } from '../v2/knowledge-cards/feature-cards/JiraNlToJqlCard/JiraNlToJqlCard';
import { JiraNlToJqlSmartlinkCard } from '../v2/knowledge-cards/feature-cards/JiraNlToJqlCard/JiraNlToJqlSmartlinkCard';
import { TopicKnowledgeCard } from '../v2/knowledge-cards/feature-cards/TopicKnowledgeCard';

import { AgentKnowledgeCard } from './agent-knowledge-card/agent-knowledge-card';
import { KnowledgeCardLoading } from './knowledge-card-loading';
import { PersonKnowledgeCard } from './person-knowledge-card/person-knowledge-card';
import { TeamKnowledgeCard } from './team-knowledge-card/team-knowledge-card';

export const KnowledgeCard = ({
	onFMPReached,
}: {
	onFMPReached: () => void;
}): React.JSX.Element | null => {
	const {
		isAnswered,
		hasError,
		smartAnswersRoute,
		loading,
		commonAttributes,
		baseQuery,
		sourceProduct,
	} = useAIAnswerContext();
	const { cloudId, brand, searchSessionId, workspaceId, query } = commonAttributes;

	// Jira NL to JQL Card doesn't populate the context with hasError, isAnswered so we use a separate condition
	if (smartAnswersRoute === QueryIntent.JIRA_NATURAL_LANGUAGE_QUERY) {
		const showJiraSAINCardSmartlink = SAINFeatureControl.isJiraSAINCardSmartLinkEnabled({
			sourceProduct,
		});
		if (showJiraSAINCardSmartlink) {
			return (
				<JiraNlToJqlSmartlinkCard
					query={baseQuery ?? ''} // baseQuery here is the original query from the user
					cloudId={cloudId}
					aiBrand={brand}
					searchSessionId={searchSessionId}
					workspaceId={workspaceId}
					product={sourceProduct ?? ''}
					onFMPReached={onFMPReached}
				/>
			);
		} else {
			return (
				<JiraNlToJqlCard
					title={query} // query here is overridden with the transformed query in FPS
					query={baseQuery ?? ''} // baseQuery here is the original query from the user
					cloudId={cloudId}
					aiBrand={brand}
					searchSessionId={searchSessionId}
					workspaceId={workspaceId}
					product={sourceProduct ?? ''}
					onFMPReached={onFMPReached}
				/>
			);
		}
	}

	if (loading) {
		return <KnowledgeCardLoading queryIntent={smartAnswersRoute} />;
	}

	if (isAnswered && !hasError) {
		switch (smartAnswersRoute) {
			case QueryIntent.TEAM:
				return <TeamKnowledgeCard onFMPReached={onFMPReached} />;
			case QueryIntent.PERSON:
				return <PersonKnowledgeCard onFMPReached={onFMPReached} />;
			case QueryIntent.TOPIC:
				return <TopicKnowledgeCard onFMPReached={onFMPReached} />;
			case QueryIntent.AGENT:
				return <AgentKnowledgeCard onFMPReached={onFMPReached} />;
			case QueryIntent.ORG_CHART:
				return <PersonKnowledgeCard onFMPReached={onFMPReached} />;
			default:
				return null;
		}
	}

	return null;
};
